import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedRoute } from './ProtectedRoute';
import { ROUTES, USER_ROLES } from '../utils/constants';

// Auth Pages
import { LoginPage } from '../pages/auth/LoginPage';
import { RegisterPage } from '../pages/auth/RegisterPage';
import { ForgotPasswordPage } from '../pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/auth/ResetPasswordPage';
import { InviteVerifyPage } from '../pages/auth/InviteVerifyPage';
import { InviteAcceptPage } from '../pages/auth/InviteAcceptPage';
import { LmsLaunchPage } from '../pages/lms/LmsLaunchPage';
import { AuthenticatePage } from '../pages/auth/AuthenticatePage';
import { BanyanProEmbedPage } from '../integrations/banyanpro/embed/BanyanProEmbedPage';
import { LandingPage } from '../pages/LandingPage';
import { ModulesPage } from '../pages/landing/ModulesPage';
import { TeacherLandingPage } from '../pages/landing/TeacherLandingPage';
import { PrivacyPage } from '../pages/landing/PrivacyPage';
import Grade3AssignmentPage from '../pages/Grade3AssignmentPage';

// Dashboard Pages
import { DevDashboard } from '../pages/devteam/Dashboard';
import { SchoolsList } from '../pages/devteam/SchoolsList';
import { CreateSchool } from '../pages/devteam/CreateSchool';
import { SchoolDetails } from '../pages/devteam/SchoolDetails';
import { AuditLogs } from '../pages/devteam/AuditLogs';
import { InviteDevTeam } from '../pages/devteam/InviteDevTeam';
import { PlansList } from '../pages/devteam/PlansList';
import { PromptTemplatePage } from '../pages/devteam/PromptTemplatePage';

import { AdminDashboard } from '../pages/admin/Dashboard';
import { SchoolProfile } from '../pages/admin/SchoolProfile';
import { SchoolSetup } from '../pages/admin/SchoolSetup';
import { ClassesList } from '../pages/admin/ClassesList';
import { StudentsList } from '../pages/admin/StudentsList';
import { TeachersList } from '../pages/admin/TeachersList';
import TeacherDetails from '../pages/admin/TeacherDetails';
import { ParentsList } from '../pages/admin/ParentsList';
import { BillingPlansPage } from '../pages/admin/BillingPlansPage';
import { CreateStudent } from '../pages/admin/CreateStudent';
import { StudentViewPage } from '../pages/admin/StudentViewPage';
import { SectionsPage } from '../pages/admin/SectionsPage';
import { InviteTeacher } from '../pages/admin/InviteTeacher';
import { InviteParent } from '../pages/admin/InviteParent';
import { InviteStudent } from '../pages/admin/InviteStudent';

import { TeacherDashboard } from '../pages/teacher/Dashboard';
import { TeacherProfile } from '../pages/teacher/Profile';
import { MySectionsPage } from '../pages/teacher/MySectionsPage';
import { AssignmentsPage } from '../pages/teacher/Assignments';
import { ReviewSubmissionPage } from '../pages/teacher/ReviewSubmissionPage';
import { AnalyticsPage } from '../pages/teacher/Analytics';
import { TeacherTutorAnalyticsPage } from '../pages/teacher/TeacherTutorAnalytics';
import { TeacherStudentAnalyticsPage } from '../pages/teacher/TeacherStudentAnalytics';
import LessonCanDoPage from '../pages/teacher/LessonCanDo';
import TeacherStudentCommunicationProgressPage from '../pages/teacher/TeacherStudentCommunicationProgress';
import CommunicationAnalyticsPage from '../pages/teacher/CommunicationAnalytics';
import CommunicationAlertsPage from '../pages/teacher/CommunicationAlerts';
import TeacherScenarioCanDoPage from '../pages/teacher/TeacherScenarioCanDo';
import { SettingsPage } from '../pages/teacher/Settings';

import { StudentDashboard } from '../pages/student/Dashboard';
import { StudentProfile } from '../pages/student/Profile';
import { StudentActivityPage } from '../pages/student/StudentActivityPage';
import { ConversationPage } from '../pages/student/Conversation';
import { TamilTutorPage } from '../pages/student/TamilTutor';
import { GamesPage } from '../pages/student/Games';
import TamilPhonicsFlashcards from '../pages/student/PhonicsLab';
import { StoryStudioPage } from '../pages/student/StoryStudio';
import { AITutorAvatarPage } from '../pages/student/AITutorAvatar';
import PictureTalkListPage from '../pages/student/PictureTalkListPage';
import PictureTalkScenePage from '../pages/student/PictureTalkScenePage';
import { WritingPage } from '../pages/student/Writing';
import { WritingHistoryPage } from '../pages/student/WritingHistoryPage';
import { LibraryPage } from '../pages/student/Library';
import { ProgressPage } from '../pages/student/Progress';
import { StudentSettingsPage } from '../pages/student/StudentSettings';
import { StudentAssignmentsPage } from '../pages/student/StudentAssignments';
import { StudentCourses } from '../pages/student/StudentCourses';
import { GradePartsList } from '../pages/student/GradePartsList';
import { StudentCoursePlayer } from '../pages/student/StudentCoursePlayer';
import AiTutorLivePage from '../pages/student/AiTutorLive';
import CommunicationTutorPage from '../pages/student/CommunicationTutor';
import ScenarioConversationPage from '../pages/student/ScenarioConversation';
import ScenarioClassroomPage from '../pages/student/ScenarioClassroom';
import { TutorAnalyticsPage } from '../pages/student/TutorAnalytics';
import { StudentAnalyticsPage } from '../pages/student/Analytics';
import { StudentAnalyticsChartsPage } from '../pages/student/AnalyticsCharts';

import { ParentDashboard } from '../pages/parent/Dashboard';
import { LinkChild } from '../pages/parent/LinkChild';
import { ChildrenList } from '../pages/parent/ChildrenList';
import ParentCommunicationProgressPage from '../pages/parent/ParentCommunicationProgress';

import { TeacherProfilePage } from '../pages/onboarding/TeacherProfilePage';
import { StudentProfilePage } from '../pages/onboarding/StudentProfilePage';
import { EmailProviderConfigsPage } from '../pages/admin/comms/EmailProviderConfigsPage';
import { EmailTemplatesPage } from '../pages/admin/comms/EmailTemplatesPage';
import { EmailTemplateBuilderPage } from '../pages/admin/comms/EmailTemplateBuilderPage';
import { ProfilePage } from '../pages/ProfilePage';
import { DevCouponsPage } from '../pages/profile/DevCouponsPage';
import { StudentSchoolFeatureGate } from '../components/guards/StudentSchoolFeatureGate';
import { StudentActivityGradeGate } from '../components/guards/StudentActivityGradeGate';

export const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  const getDefaultRoute = () => {
    if (!user) return ROUTES.LOGIN;
    if (user.roles.includes(USER_ROLES.DEV_TEAM)) return ROUTES.DEV_DASHBOARD;
    if (user.roles.includes(USER_ROLES.SCHOOL_ADMIN)) return ROUTES.ADMIN_DASHBOARD;
    if (user.roles.includes(USER_ROLES.TEACHER)) return ROUTES.TEACHER_DASHBOARD;
    if (user.roles.includes(USER_ROLES.STUDENT)) return ROUTES.STUDENT_DASHBOARD;
    if (user.roles.includes(USER_ROLES.PARENT)) return ROUTES.PARENT_DASHBOARD;
    return ROUTES.LOGIN;
  };

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path={ROUTES.LANDING}
        element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <LandingPage />}
      />
      <Route
        path={ROUTES.LOGIN}
        element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <LoginPage />}
      />
      <Route
        path={ROUTES.REGISTER}
        element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <RegisterPage />}
      />
      <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
      <Route path={ROUTES.INVITE_VERIFY} element={<InviteVerifyPage />} />
      <Route path={ROUTES.INVITE_ACCEPT} element={<InviteAcceptPage />} />
      <Route path={ROUTES.LMS_LAUNCH} element={<LmsLaunchPage />} />
      <Route path={ROUTES.AUTHENTICATE} element={<AuthenticatePage />} />
      <Route path={ROUTES.EMBED_BANYANPRO} element={<BanyanProEmbedPage />} />
      <Route path={ROUTES.MODULES} element={<ModulesPage />} />
      <Route path={ROUTES.TEACHER_LANDING} element={<TeacherLandingPage />} />
      <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
      <Route path={ROUTES.GRADE3_ASSIGNMENTS_DEMO} element={<Grade3AssignmentPage />} />

      {/* Profile (header); Coupons only for DEV_TEAM */}
      <Route
        path={ROUTES.PROFILE_COUPONS}
        element={
          <ProtectedRoute requiredRole={USER_ROLES.DEV_TEAM}>
            <DevCouponsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PROFILE}
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Dev Team Routes */}
      <Route
        path="/dev/*"
        element={
          <ProtectedRoute requiredRole={USER_ROLES.DEV_TEAM}>
            <Routes>
              <Route path="dashboard" element={<DevDashboard />} />
              <Route path="schools" element={<SchoolsList />} />
              <Route path="schools/new" element={<Navigate to={`${ROUTES.DEV_SCHOOLS}?create=1`} replace />} />
              <Route path="schools/:id" element={<SchoolDetails />} />
              <Route path="comms/email-configs" element={<EmailProviderConfigsPage />} />
              <Route path="comms/email-templates" element={<EmailTemplatesPage />} />
              <Route path="comms/email-templates/new" element={<EmailTemplateBuilderPage />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="plans" element={<PlansList />} />
              <Route path="prompt-templates" element={<PromptTemplatePage />} />
              <Route path="invite" element={<InviteDevTeam />} />
              <Route path="*" element={<Navigate to={ROUTES.DEV_DASHBOARD} replace />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* School Admin Routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredRole={USER_ROLES.SCHOOL_ADMIN}>
            <Routes>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="school" element={<SchoolProfile />} />
              <Route path="setup" element={<SchoolSetup />} />
              <Route path="classes" element={<ClassesList />} />
              <Route path="sections" element={<SectionsPage />} />
              <Route path="students" element={<StudentsList />} />
              <Route path="teachers" element={<TeachersList />} />
              <Route path="teachers/:teacherId" element={<TeacherDetails />} />
              <Route path="parents" element={<ParentsList />} />
              <Route path="billing" element={<BillingPlansPage />} />
              <Route path="students/new" element={<CreateStudent />} />
              <Route path="students/:id" element={<StudentViewPage />} />
              <Route path="invites/teacher" element={<InviteTeacher />} />
              <Route path="invites/parent" element={<InviteParent />} />
              <Route path="invites/student" element={<InviteStudent />} />
              <Route path="comms/email-configs" element={<EmailProviderConfigsPage />} />
              <Route path="comms/email-templates" element={<EmailTemplatesPage />} />
              <Route path="comms/email-templates/new" element={<EmailTemplateBuilderPage />} />
              <Route path="*" element={<Navigate to={ROUTES.ADMIN_DASHBOARD} replace />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* Teacher Routes */}
      <Route
        path="/teacher/*"
        element={
          <ProtectedRoute requiredRole={USER_ROLES.TEACHER}>
            <Routes>
              <Route path="dashboard" element={<TeacherDashboard />} />
              <Route path="profile" element={<TeacherProfile />} />
              <Route path="assignments" element={<AssignmentsPage />} />
              <Route path="submissions/:submissionId/review" element={<ReviewSubmissionPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="tutor-analytics" element={<TeacherTutorAnalyticsPage />} />
              <Route path="tutor-analytics/student/:studentId" element={<TeacherStudentAnalyticsPage />} />
              <Route path="can-do" element={<LessonCanDoPage />} />
              <Route
                path="communication-assignments"
                element={<Navigate to={ROUTES.TEACHER_ASSIGNMENTS_COMM_TASKS} replace />}
              />
              <Route path="communication-analytics" element={<CommunicationAnalyticsPage />} />
              <Route path="communication-alerts" element={<CommunicationAlertsPage />} />
              <Route path="scenario-can-do" element={<TeacherScenarioCanDoPage />} />
              <Route path="student-progress" element={<TeacherStudentCommunicationProgressPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="my-sections" element={<MySectionsPage />} />
              <Route path="*" element={<Navigate to={ROUTES.TEACHER_DASHBOARD} replace />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* Student Routes */}
      <Route
        path="/student/*"
        element={
          <ProtectedRoute requiredRole={USER_ROLES.STUDENT}>
            <Routes>
              <Route path="dashboard" element={<StudentDashboard />} />
              <Route path="courses" element={<StudentCourses />} />
              <Route path="grade/:grade/parts" element={<GradePartsList />} />
              <Route path="courses/:courseId" element={<StudentCoursePlayer />} />
              <Route
                path="profile/activity"
                element={<Navigate to={ROUTES.STUDENT_ACTIVITY} replace />}
              />
              <Route path="profile" element={<StudentProfile />} />
              <Route
                path="activity"
                element={
                  <StudentActivityGradeGate>
                    <StudentActivityPage />
                  </StudentActivityGradeGate>
                }
              />
              <Route path="conversation" element={<ConversationPage />} />
              <Route
                path="tamil-tutor"
                element={
                  <StudentSchoolFeatureGate featureKey="ai_tutor">
                    <TamilTutorPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="ai-tutor-live"
                element={
                  <StudentSchoolFeatureGate featureKey="ai_tutor">
                    <AiTutorLivePage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="communication-tutor"
                element={
                  <StudentSchoolFeatureGate featureKey="communication_tutor">
                    <CommunicationTutorPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="scenario-conversation"
                element={
                  <StudentSchoolFeatureGate featureKey="scenario_based_convo">
                    <ScenarioConversationPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="scenario-conversation/classroom"
                element={
                  <StudentSchoolFeatureGate featureKey="scenario_based_convo">
                    <ScenarioClassroomPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="communication-assignments"
                element={
                  <StudentSchoolFeatureGate featureKey="communication_tutor">
                    <Navigate to={ROUTES.STUDENT_ASSIGNMENTS_COMMUNICATION_TAB} replace />
                  </StudentSchoolFeatureGate>
                }
              />

              <Route path="games" element={<GamesPage />} />
              <Route path="phonics-lab" element={<TamilPhonicsFlashcards />} />
              <Route path="assignments" element={<StudentAssignmentsPage />} />
              <Route
                path="story-studio"
                element={
                  <StudentSchoolFeatureGate featureKey="story_studio">
                    <StoryStudioPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route path="ai-tutor-avatar" element={<AITutorAvatarPage />} />
              <Route
                path="picture-talk/:sceneId"
                element={
                  <StudentSchoolFeatureGate featureKey="picture_talk">
                    <PictureTalkScenePage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="picture-talk"
                element={
                  <StudentSchoolFeatureGate featureKey="picture_talk">
                    <PictureTalkListPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="writing/history"
                element={
                  <StudentSchoolFeatureGate featureKey="grammar">
                    <WritingHistoryPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route
                path="writing"
                element={
                  <StudentSchoolFeatureGate featureKey="grammar">
                    <WritingPage />
                  </StudentSchoolFeatureGate>
                }
              />
              <Route path="library" element={<LibraryPage />} />
              <Route path="progress" element={<ProgressPage />} />
              <Route path="analytics/charts" element={<StudentAnalyticsChartsPage />} />
              <Route path="analytics" element={<StudentAnalyticsPage />} />
              <Route path="tutor-analytics" element={<TutorAnalyticsPage />} />
              <Route path="settings" element={<StudentSettingsPage />} />
              <Route path="*" element={<Navigate to={ROUTES.STUDENT_DASHBOARD} replace />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* Parent Routes */}
      <Route
        path="/parent/*"
        element={
          <ProtectedRoute requiredRole={USER_ROLES.PARENT}>
            <Routes>
              <Route path="dashboard" element={<ParentDashboard />} />
              <Route path="link-child" element={<LinkChild />} />
              <Route path="children" element={<ChildrenList />} />
              <Route path="progress" element={<ParentCommunicationProgressPage />} />
              <Route path="*" element={<Navigate to={ROUTES.PARENT_DASHBOARD} replace />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* Onboarding Routes */}
      <Route
        path="/onboarding/*"
        element={
          <ProtectedRoute>
            <Routes>
              <Route path="school-setup" element={<SchoolSetup />} />
              <Route path="teacher-profile" element={<TeacherProfilePage />} />
              <Route path="student-profile" element={<StudentProfilePage />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<Navigate to={ROUTES.LANDING} replace />} />
    </Routes>
  );
};

